/**
 * ScaleColoringPanel - Configure automatic LED coloring based on musical scale
 * Spec §5.14: Scale coloring (USER-EDITABLE)
 */

import { useState } from 'react';
import { useConfigStore, selectMappingMode } from '@/store/configStore';
import { getNoteName } from '@/lib/noteMapping';
import { rgbToHex, hexToRgb } from '@/lib/colorUtils';
import type { ScaleType } from '@/types/config';

const SCALE_TYPE_LABELS: Record<ScaleType, string> = {
  major: 'Major',
  natural_minor: 'Natural Minor',
  harmonic_minor: 'Harmonic Minor',
  melodic_minor: 'Melodic Minor',
  pentatonic_major: 'Pentatonic Major',
  pentatonic_minor: 'Pentatonic Minor',
  blues: 'Blues',
  dorian: 'Dorian',
  phrygian: 'Phrygian',
  lydian: 'Lydian',
  mixolydian: 'Mixolydian',
  whole_tone: 'Whole Tone',
  chromatic: 'Chromatic',
};

export function ScaleColoringPanel() {
  const scaleColoring = useConfigStore((state) => state.scaleColoring);
  const ledEnabled = useConfigStore((state) => state.ledSettings.enabled);
  const setEnabled = useConfigStore((state) => state.setScaleColoringEnabled);
  const setRootNote = useConfigStore((state) => state.setScaleColoringRootNote);
  const setScaleType = useConfigStore((state) => state.setScaleType);
  const setRootColor = useConfigStore((state) => state.setScaleColoringRootColor);
  const setInScaleColor = useConfigStore((state) => state.setScaleColoringInScaleColor);
  const setNonScaleColor = useConfigStore((state) => state.setScaleColoringNonScaleColor);
  const refreshColors = useConfigStore((state) => state.refreshColors);

  // Microtonal tuning (v2.0)
  const mappingMode = useConfigStore(selectMappingMode);
  const setRootDegree = useConfigStore((state) => state.setScaleColoringRootDegree);

  // Per-degree coloring (v2.0)
  const setColorMode = useConfigStore((state) => state.setScaleColoringMode);
  const setDegreeColor = useConfigStore((state) => state.setDegreeColor);
  const initDegreeColors = useConfigStore((state) => state.initDegreeColors);
  const initSpectrumColors = useConfigStore((state) => state.initSpectrumColors);

  const [refreshing, setRefreshing] = useState(false);
  const [spectrumInterval, setSpectrumInterval] = useState(7);
  const [spectrumFillAll, setSpectrumFillAll] = useState(false);
  const [spectrumCycleShift, setSpectrumCycleShift] = useState(1);

  const rootNoteName = getNoteName(scaleColoring.root_note);
  const isEnabled = ledEnabled && scaleColoring.enabled;

  const rootHex = rgbToHex(scaleColoring.colors.root);
  const inScaleHex = rgbToHex(scaleColoring.colors.in_scale);
  const nonScaleHex = rgbToHex(scaleColoring.colors.non_scale);

  // Degree count and labels for per-degree grid
  const CHROMATIC_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const degreeCount = useConfigStore((state) => state.tuningTable?.notes_per_period) ?? 12;
  const degreeLabels: string[] = mappingMode === 'microtonal'
    ? Array.from({ length: degreeCount }, (_, i) => String(i))
    : CHROMATIC_NAMES.map((_, i) => {
        // Show pitch class relative to root note
        const idx = (scaleColoring.root_note + i) % 12;
        return CHROMATIC_NAMES[idx] ?? String(i);
      });

  const handleRefresh = () => {
    setRefreshing(true);
    refreshColors();
    setTimeout(() => setRefreshing(false), 300);
  };

  return (
    <>
      {/* Enable checkbox - Spec §5.14 */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            cursor: ledEnabled ? 'pointer' : 'not-allowed',
          }}
        >
          <input
            type="checkbox"
            checked={scaleColoring.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!ledEnabled}
            style={{ cursor: ledEnabled ? 'pointer' : 'not-allowed' }}
          />
          Enable scale coloring
        </label>
        {!ledEnabled && (
          <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            (LEDs must be enabled in LED Settings)
          </div>
        )}
        {scaleColoring.enabled && (
          <div style={{ fontSize: '12px', color: '#e67e22', marginTop: '4px' }}>
            Layer coloring is automatically disabled when scale coloring is active.
          </div>
        )}
      </div>

      {/* Scale Type Selector - hidden when microtonal tuning active */}
      {mappingMode === 'note_mapping' && (
        <div style={{ marginBottom: '16px' }}>
          <label
            htmlFor="scale-type"
            style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
          >
            Scale Type:
          </label>
          <select
            id="scale-type"
            value={scaleColoring.scale_type}
            onChange={(e) => setScaleType(e.target.value as ScaleType)}
            disabled={!isEnabled}
            style={{
              width: '100%',
              padding: '6px 8px',
              fontSize: '14px',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              cursor: isEnabled ? 'pointer' : 'not-allowed',
              backgroundColor: isEnabled ? '#fff' : '#f8f9fa',
            }}
          >
            {(Object.keys(SCALE_TYPE_LABELS) as ScaleType[]).map((key) => (
              <option key={key} value={key}>
                {SCALE_TYPE_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Root Note / Root Degree Selector */}
      <div style={{ marginBottom: '16px' }}>
        {mappingMode === 'microtonal' ? (
          <>
            <label
              htmlFor="root-degree"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              Root Degree: {scaleColoring.root_degree ?? 0} (of {degreeCount})
            </label>
            <input
              id="root-degree"
              type="range"
              min="0"
              max={degreeCount - 1}
              value={scaleColoring.root_degree ?? 0}
              onChange={(e) => setRootDegree(parseInt(e.target.value))}
              disabled={!isEnabled}
              style={{
                width: '100%',
                cursor: isEnabled ? 'pointer' : 'not-allowed',
              }}
            />
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
              Scale mask from tuning table ({degreeCount}-EDO)
            </div>
          </>
        ) : (
          <>
            <label
              htmlFor="root-note"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              Root Note: {rootNoteName} (MIDI {scaleColoring.root_note})
            </label>
            <input
              id="root-note"
              type="range"
              min="0"
              max="127"
              value={scaleColoring.root_note}
              onChange={(e) => setRootNote(parseInt(e.target.value))}
              disabled={!isEnabled}
              style={{
                width: '100%',
                cursor: isEnabled ? 'pointer' : 'not-allowed',
              }}
            />
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
              Select the root note of the scale (0-127)
            </div>
          </>
        )}
      </div>

      {/* Color Mode Toggle */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
          Color Mode
        </h4>
        <div style={{ display: 'flex', gap: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: isEnabled ? 'pointer' : 'not-allowed' }}>
            <input
              type="radio"
              name="color-mode"
              checked={scaleColoring.color_mode !== 'per_degree'}
              onChange={() => setColorMode('three_color')}
              disabled={!isEnabled}
            />
            Three-color
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: isEnabled ? 'pointer' : 'not-allowed' }}>
            <input
              type="radio"
              name="color-mode"
              checked={scaleColoring.color_mode === 'per_degree'}
              onChange={() => setColorMode('per_degree')}
              disabled={!isEnabled}
            />
            Per-degree
          </label>
        </div>
      </div>

      {/* Per-Degree Color Grid */}
      {scaleColoring.color_mode === 'per_degree' ? (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
            Degree Colors ({degreeCount})
          </h4>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            marginBottom: '8px',
          }}>
            {Array.from({ length: degreeCount }, (_, i) => {
              const color = scaleColoring.degree_colors?.[i] ?? [128, 128, 128];
              const hex = rgbToHex(color as [number, number, number]);
              const rootDeg = mappingMode === 'microtonal' ? (scaleColoring.root_degree ?? 0) : 0;
              const isRoot = i === rootDeg;
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <label
                    style={{
                      display: 'block',
                      width: '28px',
                      height: '28px',
                      backgroundColor: hex,
                      border: isRoot ? '2px solid #fff' : '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: isEnabled ? 'pointer' : 'not-allowed',
                      boxShadow: isRoot ? '0 0 0 2px #333' : 'none',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <input
                      type="color"
                      value={hex}
                      onChange={(e) => setDegreeColor(i, hexToRgb(e.target.value))}
                      disabled={!isEnabled}
                      style={{
                        opacity: 0,
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        cursor: isEnabled ? 'pointer' : 'not-allowed',
                      }}
                    />
                  </label>
                  <span style={{ fontSize: '9px', color: '#6c757d', marginTop: '1px' }}>
                    {degreeLabels[i] ?? i}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => initDegreeColors()}
              disabled={!isEnabled}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                color: '#fff',
                backgroundColor: isEnabled ? '#6c757d' : '#adb5bd',
                border: 'none',
                borderRadius: '4px',
                cursor: isEnabled ? 'pointer' : 'not-allowed',
              }}
            >
              Init from 3-color
            </button>
          </div>
          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px', marginBottom: '10px' }}>
            Regenerates palette from three-color scheme + scale mask
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Interval:
              <input
                type="number"
                min={1}
                max={Math.max(1, degreeCount - 1)}
                value={spectrumInterval}
                onChange={(e) => setSpectrumInterval(Math.max(1, Math.min(degreeCount - 1, parseInt(e.target.value) || 1)))}
                disabled={!isEnabled}
                style={{
                  width: '48px',
                  padding: '3px 6px',
                  fontSize: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  textAlign: 'center',
                }}
              />
            </label>
            <button
              onClick={() => initSpectrumColors(spectrumInterval, spectrumFillAll, spectrumCycleShift)}
              disabled={!isEnabled}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                color: '#fff',
                backgroundColor: isEnabled ? '#17a2b8' : '#adb5bd',
                border: 'none',
                borderRadius: '4px',
                cursor: isEnabled ? 'pointer' : 'not-allowed',
              }}
            >
              Generate spectrum
            </button>
          </div>
          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
            Rainbow hues walking by interval (7 = fifths, 4 = major thirds, 1 = chromatic)
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: isEnabled ? 'pointer' : 'not-allowed' }}>
              <input
                type="checkbox"
                checked={spectrumFillAll}
                onChange={(e) => setSpectrumFillAll(e.target.checked)}
                disabled={!isEnabled}
              />
              Fill all degrees
            </label>
            {spectrumFillAll && (
              <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Cycle shift:
                <input
                  type="number"
                  min={1}
                  max={Math.max(1, degreeCount - 1)}
                  value={spectrumCycleShift}
                  onChange={(e) => setSpectrumCycleShift(Math.max(1, Math.min(degreeCount - 1, parseInt(e.target.value) || 1)))}
                  disabled={!isEnabled}
                  style={{
                    width: '48px',
                    padding: '3px 6px',
                    fontSize: '12px',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}
                />
              </label>
            )}
          </div>
          {spectrumFillAll && (
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
              When interval doesn't cover all degrees, continues with shifted cycles until all are colored
            </div>
          )}
        </div>
      ) : (
        /* Three-Color Pickers - root, in-scale, non-scale */
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
            Scale Colors
          </h4>

          {/* Root Color */}
          <div style={{ marginBottom: '8px' }}>
            <label
              htmlFor="root-color"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              Root:
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                id="root-color"
                type="color"
                value={rootHex}
                onChange={(e) => setRootColor(hexToRgb(e.target.value))}
                disabled={!isEnabled}
                style={{
                  width: '60px',
                  height: '36px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  cursor: isEnabled ? 'pointer' : 'not-allowed',
                }}
              />
              <span style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
                RGB({scaleColoring.colors.root[0]}, {scaleColoring.colors.root[1]}, {scaleColoring.colors.root[2]})
              </span>
            </div>
          </div>

          {/* In-Scale Color */}
          <div style={{ marginBottom: '8px' }}>
            <label
              htmlFor="in-scale-color"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              In Scale:
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                id="in-scale-color"
                type="color"
                value={inScaleHex}
                onChange={(e) => setInScaleColor(hexToRgb(e.target.value))}
                disabled={!isEnabled}
                style={{
                  width: '60px',
                  height: '36px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  cursor: isEnabled ? 'pointer' : 'not-allowed',
                }}
              />
              <span style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
                RGB({scaleColoring.colors.in_scale[0]}, {scaleColoring.colors.in_scale[1]}, {scaleColoring.colors.in_scale[2]})
              </span>
            </div>
          </div>

          {/* Non-Scale Color */}
          <div style={{ marginBottom: '8px' }}>
            <label
              htmlFor="non-scale-color"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              Non-Scale:
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                id="non-scale-color"
                type="color"
                value={nonScaleHex}
                onChange={(e) => setNonScaleColor(hexToRgb(e.target.value))}
                disabled={!isEnabled}
                style={{
                  width: '60px',
                  height: '36px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  cursor: isEnabled ? 'pointer' : 'not-allowed',
                }}
              />
              <span style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
                RGB({scaleColoring.colors.non_scale[0]}, {scaleColoring.colors.non_scale[1]}, {scaleColoring.colors.non_scale[2]})
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={handleRefresh}
          disabled={!ledEnabled}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: refreshing ? '#28a745' : '#007bff',
            border: 'none',
            borderRadius: '4px',
            cursor: ledEnabled ? 'pointer' : 'not-allowed',
            transition: 'background-color 0.2s',
            opacity: ledEnabled ? 1 : 0.5,
          }}
          onMouseEnter={(e) => {
            if (ledEnabled && !refreshing) {
              e.currentTarget.style.backgroundColor = '#0056b3';
            }
          }}
          onMouseLeave={(e) => {
            if (ledEnabled && !refreshing) {
              e.currentTarget.style.backgroundColor = '#007bff';
            }
          }}
        >
          {refreshing ? 'Colors Refreshed!' : 'Refresh Colors'}
        </button>
      </div>

      {/* Informational note */}
      <div
        style={{
          padding: '8px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#6c757d',
        }}
      >
        Scale coloring highlights notes based on the selected scale. Root notes get the root color, notes in the scale get the in-scale color, and all other notes get the non-scale color.
      </div>
    </>
  );
}
